/**
 * The sites of the platform (11 §8.1, ADR 0047): one Worker serves tarock.game and tile.game. Shared by the Worker
 * (host routing, origins, mail) and the web app (brand, routes, the service worker's precache). Pure data and
 * functions (`@tarock/protocol/sites`).
 */

/** A site: a host with its own brand and game catalogue (ADR 0046). */
export type SiteId = 'tarock' | 'tile';

/** A game id (ADR 0046). */
export type GameId =
  | 'zwanzigerrufen'
  | 'dominoes'
  | 'triominoes'
  | 'mexican-train'
  | 'quadominoes'
  | 'hexominoes'
  | 'spherominoes'
  | 'tile-rummy';

/** The environments of the Worker. */
export type SiteEnvironment = 'production' | 'staging' | 'development' | 'test';

/** One site's configuration. */
export interface SiteConfig {
  readonly id: SiteId;
  /** The brand, as written in the UI and in mail (`tarock.game`, `tile.game`). */
  readonly name: string;
  readonly hosts: { readonly production: string; readonly staging: string; readonly dev: string };
  /** The games of the site, in menu order (11 §8.1). */
  readonly games: readonly GameId[];
  readonly defaultGame: GameId;
  /** The mail domain of the sender `noreply@<domain>` (B-007 onboarded tile.game; contacts stay tarock.game's, Q8). */
  readonly mailDomain: string;
  readonly theme: { readonly themeColor: string; readonly background: string };
  /** The site's one-line description (HTML meta, manifest). */
  readonly description: string;
}

/** Both sites. */
export const SITES: Readonly<Record<SiteId, SiteConfig>> = {
  tarock: {
    id: 'tarock',
    name: 'tarock.game',
    hosts: { production: 'tarock.game', staging: 'staging.tarock.game', dev: 'tarock.localhost' },
    games: ['zwanzigerrufen'],
    defaultGame: 'zwanzigerrufen',
    mailDomain: 'tarock.game',
    theme: { themeColor: '#0b1f14', background: '#0b1f14' },
    description: 'Austrian Tarock (Zwanzigerrufen) with friends and computer players.',
  },
  tile: {
    id: 'tile',
    name: 'tile.game',
    hosts: { production: 'tile.game', staging: 'staging.tile.game', dev: 'tile.localhost' },
    games: ['dominoes', 'triominoes', 'mexican-train', 'quadominoes', 'hexominoes', 'spherominoes', 'tile-rummy'],
    defaultGame: 'dominoes',
    mailDomain: 'tile.game',
    theme: { themeColor: '#1d2a3a', background: '#1d2a3a' },
    description: 'Dominoes, Triominoes and Mexican Train with family, friends and computer players.',
  },
};

/** The site of each game. */
export const GAME_SITE: Readonly<Record<GameId, SiteId>> = {
  zwanzigerrufen: 'tarock',
  dominoes: 'tile',
  triominoes: 'tile',
  quadominoes: 'tile',
  hexominoes: 'tile',
  spherominoes: 'tile',
  'tile-rummy': 'tile',
  'mexican-train': 'tile',
};

/** The site of a request's host (without a port); unknown hosts (localhost, 127.0.0.1, workers.dev) are tarock's. */
export function siteForHost(host: string): SiteId {
  const name = host.replace(/:\d+$/, '').toLowerCase();
  const tile = SITES.tile.hosts;
  return name === tile.production || name === tile.staging || name === tile.dev ? 'tile' : 'tarock';
}

/** The origin of a site in an environment; dev and test keep the port of `devOrigin` (e.g. `http://localhost:8787`). */
export function siteOrigin(site: SiteId, env: SiteEnvironment, devOrigin: string): string {
  if (env === 'production') {
    return `https://${SITES[site].hosts.production}`;
  }
  if (env === 'staging') {
    return `https://${SITES[site].hosts.staging}`;
  }
  const [, protocol = 'http:', hostname = 'localhost', port] =
    /^([a-z]+:)\/\/([^/:]+)(?::(\d+))?/i.exec(devOrigin) ?? [];
  if (site === 'tarock' && !hostname.endsWith('.localhost')) {
    return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  }
  return `${protocol}//${SITES[site].hosts.dev}${port ? `:${port}` : ''}`;
}
